import { Checkbox, FormControlLabel, MenuItem, TextField } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { US_STATES } from '@app/shared';
import type { CarrierDto, GroupOptionDto, ReportDef } from '@app/shared';
import { AgentChainSelect, deepestAgent } from '../../components/AgentChainSelect';
import type { AgentChain } from '../../components/AgentChainSelect';
import { api } from '../../lib/api';
import { useFeatureReleased } from '../../release';
import type { Query } from '../../lib/api';

export type FilterDraft = Record<string, string>;

/**
 * Turns what the user entered into the query the report API takes, and names the required
 * selects that are still empty.
 */
export function filtersToQuery(
  def: ReportDef,
  draft: FilterDraft,
  chain: AgentChain,
): { query: Query; problems: Record<string, string> } {
  const problems: Record<string, string> = {};
  const query: Query = {};
  for (const f of def.filters) {
    if (f.type === 'agentChain') {
      query.agentId = deepestAgent(chain);
    } else if (f.type === 'dateRange') {
      query[`${f.key}From`] = draft[`${f.key}From`];
      query[`${f.key}To`] = draft[`${f.key}To`];
    } else {
      const value = (draft[f.key] ?? '').trim();
      if (f.type === 'select' && f.required && !value) problems[f.key] = `Select ${f.label}`;
      query[f.key] = value;
    }
  }
  return { query, problems };
}

/** The filter fields of a report definition. The caller lays them out and adds the button. */
export function ReportFilterFields({
  def,
  draft,
  onChange,
  chain,
  onChainChange,
  problem,
}: {
  def: ReportDef;
  draft: FilterDraft;
  onChange: (key: string, value: string) => void;
  chain: AgentChain;
  onChainChange: (chain: AgentChain) => void;
  problem: (key: string) => string;
}) {
  const featureOn = useFeatureReleased();
  const wantsCarriers = def.filters.some((f) => f.type === 'carrier');
  const carriers = useQuery({
    queryKey: ['carriers', 'report-filter'],
    queryFn: () => api.get<{ data: CarrierDto[] }>('/carriers'),
    enabled: wantsCarriers,
    staleTime: 60_000,
  });
  const wantsGroups = def.filters.some(
    (f) => f.type === 'userGroup' && (!f.feature || featureOn(f.feature)),
  );
  const groups = useQuery({
    queryKey: ['user-groups', 'options'],
    queryFn: () => api.get<{ data: GroupOptionDto[] }>('/user-groups/options'),
    enabled: wantsGroups,
    staleTime: 60_000,
  });

  return (
    <>
      {def.filters.map((f) => {
        if ('feature' in f && f.feature && !featureOn(f.feature)) return null;
        if (f.type === 'checkbox')
          return (
            <FormControlLabel
              key={f.key}
              label={f.label}
              control={
                <Checkbox
                  checked={draft[f.key] === 'Y'}
                  onChange={(e) => onChange(f.key, e.target.checked ? 'Y' : '')}
                />
              }
            />
          );
        if (f.type === 'agentChain')
          return <AgentChainSelect key="agentChain" value={chain} onChange={onChainChange} />;
        if (f.type === 'dateRange')
          return (['From', 'To'] as const).map((end) => (
            <TextField
              key={f.key + end}
              type="date"
              label={`${f.label} ${end}`}
              value={draft[`${f.key}${end}`] ?? ''}
              onChange={(e) => onChange(`${f.key}${end}`, e.target.value)}
              error={Boolean(problem(`${f.key}${end}`))}
              helperText={problem(`${f.key}${end}`) || undefined}
              slotProps={{ inputLabel: { shrink: true } }}
            />
          ));
        if (f.type === 'text')
          return (
            <TextField
              key={f.key}
              label={f.label}
              value={draft[f.key] ?? ''}
              onChange={(e) => onChange(f.key, e.target.value)}
              slotProps={{ htmlInput: { maxLength: f.maxLength } }}
            />
          );
        const options: Array<[value: string, label: string]> =
          f.type === 'state'
            ? US_STATES.map(([code, name]) => [code, `${code} - ${name}`])
            : f.type === 'carrier'
              ? (carriers.data?.data ?? []).map((x) => [x.code, x.code])
              : f.type === 'userGroup'
                ? (groups.data?.data ?? []).map((g) => [g.id, g.name])
                : f.options.map((o) => [o, o]);
        if (f.type === 'userGroup' || (f.type === 'select' && f.multiple)) {
          const label = new Map(options);
          // Nothing chosen means every option, as on the reference's multi-selects.
          const chosen = (draft[f.key] ?? '').split(',').filter(Boolean);
          return (
            <TextField
              key={f.key}
              select
              label={f.label}
              required={f.type === 'select' && f.required}
              value={chosen}
              onChange={(e) => {
                const next = e.target.value as unknown as string[] | string;
                onChange(f.key, (Array.isArray(next) ? next : next.split(',')).join(','));
              }}
              error={Boolean(problem(f.key))}
              helperText={problem(f.key) || undefined}
              slotProps={{
                select: {
                  multiple: true,
                  renderValue: (v) => (v as string[]).map((x) => label.get(x) ?? x).join(', '),
                },
              }}
              sx={{ minWidth: 200 }}
            >
              {options.map(([value, text]) => (
                <MenuItem key={value} value={value}>
                  {text}
                </MenuItem>
              ))}
            </TextField>
          );
        }
        const empty = f.type === 'state' ? f.allLabel : f.type === 'carrier' ? 'All' : f.emptyLabel;
        return (
          <TextField
            key={f.key}
            select
            label={f.label}
            required={f.type === 'select' && f.required}
            value={draft[f.key] ?? ''}
            onChange={(e) => onChange(f.key, e.target.value)}
            error={Boolean(problem(f.key))}
            helperText={problem(f.key) || undefined}
          >
            {empty !== undefined || (f.type === 'select' && f.required) ? (
              <MenuItem value="">{empty ?? '--Select--'}</MenuItem>
            ) : null}
            {options.map(([value, label]) => (
              <MenuItem key={value} value={value}>
                {label}
              </MenuItem>
            ))}
          </TextField>
        );
      })}
    </>
  );
}
